import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  People,
  AccountBalance,
  Warning,
  Event,
  EmojiEvents,
  Payment,
  CurrencyRupee,
  Receipt,
  Share,
  PictureAsPdf,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ComposedChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { ACCENTS, SERIES, tint, colorForName } from '../theme/accents';
import { MethodIndicator, StatusIndicator, categoryLabel } from '../components/Indicators';
import { formatINR } from '../utils/currency';
import { useMandalName, useDistribution, slugifyName } from '../hooks/useMandalName';
import MemberDetailDialog from '../components/MemberDetailDialog';
import MonthlyStatement from '../components/MonthlyStatement';
import StatCard from '../components/StatCard';
import PageHeader from '../components/PageHeader';

const COLORS = SERIES;

const rupee = formatINR;

// Metal the fund holds, rendered with its own surface rather than a generic chip
const METAL_SURFACE = {
  gold: 'linear-gradient(135deg, #7A5A1E 0%, #D9A441 45%, #F0C979 60%, #A87A28 100%)',
  silver: 'linear-gradient(135deg, #6B7480 0%, #B9C2CC 45%, #E3E9EF 60%, #8A939D 100%)',
};

const Dashboard = () => {
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [memberSummary, setMemberSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailMemberId, setDetailMemberId] = useState(null);
  const [arrears, setArrears] = useState(null);
  const [holdings, setHoldings] = useState(null);
  const [growth, setGrowth] = useState(null);
  const mandalName = useMandalName();
  const { title: distributionTitle } = useDistribution();
  const [statementOpen, setStatementOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // The summary PDF: fund, this month, every month, and every payment with its
  // date and time. No contact details or activity log, so any member can have it.
  const downloadSummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await axios.get('/api/dashboard/summary-report', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${slugifyName(mandalName)}-summary-${dayjs().format('YYYY-MM-DD')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Summary downloaded');
    } catch (error) {
      toast.error('Failed to prepare the summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, activityRes, summaryRes, arrearsRes, holdingsRes, growthRes] = await Promise.all([
        axios.get('/api/dashboard/stats'),
        axios.get('/api/dashboard/recent-activity'),
        axios.get('/api/dashboard/member-summary'),
        axios.get('/api/dashboard/arrears'),
        axios.get('/api/expenses/holdings'),
        axios.get('/api/dashboard/growth'),
      ]);

      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
      setMemberSummary(summaryRes.data);
      setArrears(arrearsRes.data);
      setHoldings(holdingsRes.data);
      setGrowth(growthRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  const paymentChartData = stats ? [
    { 
      name: 'Paid', 
      value: stats.currentMonth.paidMembers,
      members: stats.currentMonth.paidMembersList || []
    },
    { 
      name: 'Pending', 
      value: stats.currentMonth.pendingMembers,
      members: stats.currentMonth.pendingMembersList || []
    },
  ] : [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const membersList = data.members || [];
      const displayLimit = 10;
      
      return (
        <Paper sx={{ p: 2, maxWidth: 300, maxHeight: 400, overflow: 'auto' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            {data.name}: {data.value} members
          </Typography>
          {membersList.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="textSecondary">
                {data.name === 'Pending' ? 'Members who need to pay:' : 'Members who have paid:'}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {membersList.slice(0, displayLimit).map((member, index) => (
                  <Typography key={index} variant="body2" sx={{ py: 0.25 }}>
                    • {member.full_name}
                  </Typography>
                ))}
                {membersList.length > displayLimit && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    ... and {membersList.length - displayLimit} more
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Paper>
      );
    }
    return null;
  };

  // Each card carries its domain's hue: a wash of it behind, a bar down the edge,
  // and the figure itself in that colour.
  // Where the money is. One number cannot say this once the fund lends: cash is
  // what can be spent, lent out is with borrowers, worth is the whole position.
  const LedgerLine = () => {
    const money = stats?.money || {};
    const cash = Number(money.cashInHand) || 0;
    const lentOut = Number(money.lentOut) || 0;
    const metalValue = Number(money.metalValue) || 0;
    const worth = Number(money.fundWorth) || 0;

    // How the fund's worth is split across cash, lending and metal
    const share = (part) => (worth > 0 ? Math.max(0, (part / worth) * 100) : 0);

    return (
      <Card
        sx={{
          mb: 3,
          overflow: 'hidden',
          position: 'relative',
          borderColor: tint(ACCENTS.gold, 0.3),
          // A festival wash across the headline figure
          background: `radial-gradient(120% 140% at 0% 0%, ${tint(ACCENTS.magenta, 0.16)} 0%, rgba(0,0,0,0) 45%),
                       radial-gradient(120% 140% at 100% 0%, ${tint(ACCENTS.indigo, 0.18)} 0%, rgba(0,0,0,0) 50%),
                       linear-gradient(160deg, ${tint(ACCENTS.gold, 0.12)} 0%, rgba(0,0,0,0) 60%)`,
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline">Fund worth</Typography>
              <Typography
                className="tabular"
                sx={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontSize: { xs: '2.75rem', md: '3.5rem' },
                  fontWeight: 600,
                  lineHeight: 1.05,
                  letterSpacing: '-0.03em',
                  color: worth < 0 ? 'error.main' : 'text.primary',
                }}
              >
                {rupee(worth)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {rupee(money.contributions)} collected all time
                {money.loanInterestEarned > 0 && ` · ${rupee(money.loanInterestEarned)} interest earned`}
              </Typography>

              {/* What the worth is made of */}
              <Box sx={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', mt: 2, bgcolor: theme.palette.action.hover }}>
                <Box sx={{ width: `${share(cash)}%`, bgcolor: ACCENTS.emerald }} />
                <Box sx={{ width: `${share(lentOut)}%`, bgcolor: ACCENTS.magenta }} />
                <Box sx={{ width: `${share(metalValue)}%`, background: METAL_SURFACE.gold }} />
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              {/* Proportion of the collection already converted or spent */}
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="overline" display="block">Cash in hand</Typography>
                  <Typography className="tabular" variant="h6" sx={{ color: ACCENTS.emerald }}>
                    {rupee(cash)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">available to spend</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="overline" display="block">Lent out</Typography>
                  <Typography className="tabular" variant="h6" sx={{ color: ACCENTS.magenta }}>
                    {rupee(lentOut)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {money.loanInterestDue > 0 ? `${rupee(money.loanInterestDue)} interest due` : 'with borrowers'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="overline" display="block">Metal held</Typography>
                  <Typography className="tabular" variant="h6" sx={{ color: ACCENTS.gold }}>
                    {rupee(metalValue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {money.metalRatesSet ? 'at current rate' : 'at cost'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // Signature: what the fund owns, as metal rather than as a number
  const HoldingsStrip = () => {
    const holdings = (stats?.expensesByCategory || []).filter(
      row => ['gold', 'silver'].includes(row.category) && Number(row.quantity) > 0
    );

    if (holdings.length === 0) return null;

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" display="block" sx={{ mb: 1.5 }}>
            Held by the fund
          </Typography>
          <Grid container spacing={2}>
            {holdings.map(row => (
              <Grid item xs={12} sm={6} md={4} key={categoryLabel(row.category)}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: theme.palette.action.hover,
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: METAL_SURFACE[row.category],
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography className="tabular" variant="h6" sx={{ lineHeight: 1.2 }}>
                      {Number(row.quantity).toLocaleString('en-IN')} g
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {categoryLabel(row.category)} · {rupee(row.total)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <PageHeader
        eyebrow={dayjs().format('MMMM YYYY')}
        accent={ACCENTS.gold}
        title={mandalName}
        subtitle="Where the fund stands today — cash in hand, what is lent out, and what it is all worth."
        actions={(
          <>
            <Button variant="outlined" startIcon={<Share />} onClick={() => setStatementOpen(true)}>
              Monthly summary
            </Button>
            <Button
              variant="contained"
              startIcon={summaryLoading ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdf />}
              onClick={downloadSummary}
              disabled={summaryLoading}
            >
              {summaryLoading ? 'Preparing...' : 'Summary PDF'}
            </Button>
          </>
        )}
      />

      <LedgerLine />
      <HoldingsStrip />

      {/* The trajectory - every other figure on this page is a snapshot */}
      {growth?.series?.length > 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
              <Typography variant="overline">Fund over time</Typography>
              <Typography variant="caption" color="text.secondary">
                {growth.series.length} months · {rupee(growth.totalIn)} in, {rupee(growth.totalOut)} out
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={growth.series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="balanceFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ACCENTS.gold} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={ACCENTS.gold} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                <XAxis
                  dataKey="month"
                  tickFormatter={(m) => dayjs(`${m}-01`).format('MMM')}
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                />
                <Tooltip
                  contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 10, color: theme.palette.text.primary }}
                  labelFormatter={(m) => dayjs(`${m}-01`).format('MMMM YYYY')}
                  formatter={(value, name) => [rupee(value), name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="balance"
                  name="Balance"
                  stroke={ACCENTS.gold}
                  strokeWidth={2}
                  fill="url(#balanceFill)"
                  isAnimationActive={false}
                />
                <Bar dataKey="collected" name="Collected" fill={ACCENTS.emerald} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="spent" name="Spent" fill={ACCENTS.vermilion} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Debt carried across months, which the current-month view never showed */}
      {arrears?.totalOwed > 0 && (
        <Card sx={{ mb: 3, borderColor: tint(ACCENTS.vermilion, 0.35), background: `linear-gradient(150deg, ${tint(ACCENTS.vermilion, 0.14)} 0%, rgba(0,0,0,0) 65%)` }}>
          <CardContent>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1.5}>
              <Box>
                <Typography variant="overline" sx={{ color: ACCENTS.vermilion }}>Outstanding across all months</Typography>
                <Typography className="tabular" variant="h5" sx={{ color: ACCENTS.vermilion }}>
                  {rupee(arrears.totalOwed)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {arrears.membersInArrears} of {arrears.members.length} members
              </Typography>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {arrears.members.filter(m => m.totalOwed > 0).map(m => (
                <Chip
                  key={m.id}
                  size="small"
                  onClick={() => setDetailMemberId(m.id)}
                  label={`${m.fullName} · ${rupee(m.totalOwed)}${m.unpaidMonths > 1 ? ` (${m.unpaidMonths} months)` : ''}`}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: tint(colorForName(m.fullName), 0.16),
                    color: colorForName(m.fullName),
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Members"
            value={stats?.totalMembers || 0}
            subtitle="Contributing"
            icon={<People />}
            accent={ACCENTS.indigo}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Collected"
            value={rupee(stats?.totalFund)}
            subtitle="All time"
            icon={<AccountBalance />}
            accent={ACCENTS.emerald}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Spent"
            value={rupee(stats?.totalExpenses)}
            subtitle={`${stats?.expenseCount || 0} expense${stats?.expenseCount === 1 ? '' : 's'}`}
            icon={<Receipt />}
            accent={ACCENTS.marigold}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Penalties due"
            value={rupee(stats?.pendingPenalties?.amount)}
            subtitle={`${stats?.pendingPenalties?.count || 0} outstanding`}
            icon={<Warning />}
            accent={ACCENTS.vermilion}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Member Payment Status
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <List disablePadding>
                {memberSummary.length === 0 && (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No members yet. Add them from the Members page to start collecting.
                    </Typography>
                  </Box>
                )}
                {memberSummary.map((member) => {
                  const paid = member.payment_status === 'completed';
                  // Each member keeps one identity colour across the app
                  const personColor = colorForName(member.full_name);
                  return (
                    <ListItem
                      key={member.id}
                      disableGutters
                      onClick={() => setDetailMemberId(member.id)}
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'background-color 140ms ease',
                        '&:hover': { bgcolor: tint(personColor, 0.1) },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: tint(personColor, 0.18),
                            color: personColor,
                            border: '2px solid',
                            borderColor: paid ? ACCENTS.emerald : ACCENTS.vermilion,
                            fontWeight: 700,
                          }}
                        >
                          {member.full_name.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={member.full_name}
                        secondary={
                          paid
                            ? `Paid ${rupee(member.payment_amount)}${Number(member.penalty_amount) > 0 ? ` · ${rupee(member.penalty_amount)} penalty due` : ''}`
                            : `${rupee(member.expected_amount)} due · ${Number(member.contribution_multiplier)}× share`
                        }
                        primaryTypographyProps={{ fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <StatusIndicator status={paid ? 'completed' : 'pending'} size="small" />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Payment Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={paymentChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  innerRadius={52}
                  outerRadius={82}
                  paddingAngle={2}
                  stroke="none"
                  dataKey="value"
                  // Renders immediately; the mount animation left the arc collapsed
                  isAnimationActive={false}
                >
                  {paymentChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {stats?.nextMeeting && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Event color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Next Meeting</Typography>
                </Box>
                <Typography variant="body1">
                  Date: {dayjs(stats.nextMeeting.meeting_date).format('MMMM D, YYYY')}
                </Typography>
                <Typography variant="body1">
                  Time: {stats.nextMeeting.meeting_time}
                </Typography>
                {stats.nextMeeting.location && (
                  <Typography variant="body1">
                    Location: {stats.nextMeeting.location}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {stats?.lastDistribution && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <EmojiEvents color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">Last {distributionTitle}</Typography>
                </Box>
                <Typography variant="body1">
                  Recipient: {stats.lastDistribution.recipient_name}
                </Typography>
                <Typography variant="body1">
                  Amount: {rupee(stats.lastDistribution.amount)}
                </Typography>
                <Typography variant="body1">
                  Date: {dayjs(stats.lastDistribution.distribution_date).format('MMMM D, YYYY')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Payments
            </Typography>
            <List>
              {recentActivity?.recentPayments?.slice(0, 5).map((payment) => (
                <React.Fragment key={payment.id}>
                  <ListItem>
                    <ListItemAvatar>
                      {/* Rupee, not the dollar icon this used to show */}
                      <Avatar
                        sx={{
                          bgcolor: tint(colorForName(payment.user_name || ''), 0.2),
                          color: colorForName(payment.user_name || ''),
                          border: '1px solid',
                          borderColor: tint(colorForName(payment.user_name || ''), 0.5),
                        }}
                      >
                        <CurrencyRupee fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${payment.user_name} — ${rupee(payment.amount)}`}
                      secondary={`${dayjs(payment.payment_date).format('D MMM YYYY')} · for ${dayjs(`${payment.payment_month}-01`).format('MMM YYYY')}`}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                    {/* How it was paid, alongside whether it cleared. The
                        avatar carries member identity, so the method needs its
                        own mark rather than replacing that. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <MethodIndicator method={payment.payment_method} />
                      <StatusIndicator status={payment.status} size="small" />
                    </Box>
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <MemberDetailDialog
        memberId={detailMemberId}
        open={Boolean(detailMemberId)}
        onClose={() => setDetailMemberId(null)}
      />

      <MonthlyStatement open={statementOpen} onClose={() => setStatementOpen(false)} />
    </Box>
  );
};

export default Dashboard;